/**
 * Yelp Rating Snapshots Persistence (Story 23.5 AC #3, #14)
 *
 * Fire-and-forget D1 writer for the `yelp_rating_snapshots` table. Called
 * at the END of every successful Yelp `fetchMetrics` to build a
 * historical-time-series substrate over Yelp Fusion's snapshot-only API.
 *
 * **Dedup contract:** the unique index on `(tenantId, integrationId,
 * businessId, snapshotDate)` collapses duplicate snapshots within a single
 * UTC day to latest-write-wins via `INSERT OR REPLACE`. The cron may fire
 * `fetchMetrics` multiple times/day; only the latest write per day is kept.
 *
 * **Round 3 Red Team Attack #5 — cross-tenant snapshot isolation:** every
 * read helper MUST include `WHERE tenant_id = ?` in its query. A
 * type-level test asserts that omitting `tenantId` from the helper input
 * fails at compile time (the input interface requires it).
 *
 * **GDPR (ADR-008):** `tenantId` cascade-deletes via the FK constraint on
 * tenant erasure. The disconnect route ALSO performs an explicit DELETE
 * WHERE tenantId = ? AND integrationId = ? when a Yelp integration is
 * disconnected (per-integration GDPR purge).
 */

/**
 * Minimal D1 binding interface (Cloudflare Workers).
 * Matches the shape used by Moz's `MozD1Binding` for cross-adapter
 * consistency. Avoids depending on `@cloudflare/workers-types` at this
 * layer; the adapter envelope types it as `unknown` and the route layer
 * casts.
 */
export interface YelpD1Binding {
  prepare(query: string): YelpD1PreparedStatement;
}

export interface YelpD1PreparedStatement {
  bind(...values: unknown[]): YelpD1BoundStatement;
}

export interface YelpD1BoundStatement {
  run(): Promise<{ meta?: { changes?: number } }>;
  all<T = unknown>(): Promise<{ results?: ReadonlyArray<T> }>;
  first<T = unknown>(): Promise<T | null>;
}

// ============================================================================
// Types
// ============================================================================

export interface WriteYelpRatingSnapshotInput {
  readonly tenantId: string;
  readonly integrationId: string;
  readonly businessId: string;
  readonly rating: number;
  readonly reviewCount: number;
  /** UTC YYYY-MM-DD; defaults to today UTC. */
  readonly snapshotDate?: string;
  /** ISO 8601; defaults to now. */
  readonly capturedAt?: string;
}

export interface ReadYelpRatingSnapshotsInput {
  readonly tenantId: string;
  readonly integrationId: string;
  readonly businessId: string;
  readonly startDate: string; // YYYY-MM-DD UTC
  readonly endDate: string; // YYYY-MM-DD UTC
}

export interface ReadYelpRatingSnapshotPriorDayInput {
  readonly tenantId: string;
  readonly integrationId: string;
  readonly businessId: string;
  /** Today's UTC YYYY-MM-DD; helper returns the most recent row with snapshotDate < today. */
  readonly today: string;
}

export interface YelpRatingSnapshotRow {
  readonly id: number;
  readonly tenantId: string;
  readonly integrationId: string;
  readonly businessId: string;
  readonly snapshotDate: string;
  readonly rating: number;
  readonly reviewCount: number;
  readonly capturedAt: string;
}

// ============================================================================
// Helpers
// ============================================================================

/** Compute UTC YYYY-MM-DD from an ISO timestamp or Date. */
export function utcYyyyMmDd(input?: Date): string {
  const d = input ?? new Date();
  return d.toISOString().slice(0, 10);
}

// ============================================================================
// Write
// ============================================================================

/**
 * Insert a snapshot row with `INSERT OR REPLACE` semantics (AC #14 dedup
 * contract). Idempotent on the same-day key. Throws on D1 error (the
 * caller decides whether to wrap in fire-and-forget).
 */
export async function writeYelpRatingSnapshot(
  db: YelpD1Binding,
  input: WriteYelpRatingSnapshotInput,
): Promise<void> {
  if (!input.tenantId) throw new TypeError("writeYelpRatingSnapshot requires tenantId.");
  if (!input.integrationId)
    throw new TypeError("writeYelpRatingSnapshot requires integrationId.");
  if (!input.businessId)
    throw new TypeError("writeYelpRatingSnapshot requires businessId.");

  const snapshotDate = input.snapshotDate ?? utcYyyyMmDd();
  const capturedAt = input.capturedAt ?? new Date().toISOString();

  await db
    .prepare(
      `INSERT OR REPLACE INTO yelp_rating_snapshots (
        tenant_id, integration_id, business_id, snapshot_date, rating, review_count, captured_at
      ) VALUES (?, ?, ?, ?, ?, ?, ?)`,
    )
    .bind(
      input.tenantId,
      input.integrationId,
      input.businessId,
      snapshotDate,
      input.rating,
      input.reviewCount,
      capturedAt,
    )
    .run();
}

// ============================================================================
// Read
// ============================================================================

/**
 * Read all snapshots for an integration within a date range, ordered by
 * snapshotDate ASC. Used by `fetchMetrics` to emit `ratingSnapshot`
 * historical-trend records.
 *
 * **Cross-tenant isolation (AC #14 Round 3 Red Team Attack #5):** the
 * WHERE clause includes BOTH `tenant_id = ?` AND `integration_id = ?`. A
 * caller cannot accidentally read another tenant's snapshots even when
 * passing the same businessId.
 */
export async function readYelpRatingSnapshots(
  db: YelpD1Binding,
  input: ReadYelpRatingSnapshotsInput,
): Promise<ReadonlyArray<YelpRatingSnapshotRow>> {
  if (!input.tenantId) throw new TypeError("readYelpRatingSnapshots requires tenantId.");
  if (!input.integrationId)
    throw new TypeError("readYelpRatingSnapshots requires integrationId.");
  if (!input.businessId)
    throw new TypeError("readYelpRatingSnapshots requires businessId.");

  const result = await db
    .prepare(
      `SELECT id, tenant_id, integration_id, business_id, snapshot_date, rating, review_count, captured_at
       FROM yelp_rating_snapshots
       WHERE tenant_id = ?
         AND integration_id = ?
         AND business_id = ?
         AND snapshot_date >= ?
         AND snapshot_date <= ?
       ORDER BY snapshot_date ASC`,
    )
    .bind(
      input.tenantId,
      input.integrationId,
      input.businessId,
      input.startDate,
      input.endDate,
    )
    .all<{
      id: number;
      tenant_id: string;
      integration_id: string;
      business_id: string;
      snapshot_date: string;
      rating: number;
      review_count: number;
      captured_at: string;
    }>();

  const rows = result.results ?? [];
  return rows.map((r) => ({
    id: r.id,
    tenantId: r.tenant_id,
    integrationId: r.integration_id,
    businessId: r.business_id,
    snapshotDate: r.snapshot_date,
    rating: r.rating,
    reviewCount: r.review_count,
    capturedAt: r.captured_at,
  }));
}

/**
 * Fetch the most recent snapshot row strictly BEFORE today UTC. Used to
 * compute `ratingDelta` + `reviewCountDelta` derived aggregations.
 * Returns null when no prior snapshot exists (first-fetch path).
 */
export async function readYelpRatingSnapshotPriorDay(
  db: YelpD1Binding,
  input: ReadYelpRatingSnapshotPriorDayInput,
): Promise<YelpRatingSnapshotRow | null> {
  if (!input.tenantId)
    throw new TypeError("readYelpRatingSnapshotPriorDay requires tenantId.");
  if (!input.integrationId)
    throw new TypeError("readYelpRatingSnapshotPriorDay requires integrationId.");
  if (!input.businessId)
    throw new TypeError("readYelpRatingSnapshotPriorDay requires businessId.");

  const row = await db
    .prepare(
      `SELECT id, tenant_id, integration_id, business_id, snapshot_date, rating, review_count, captured_at
       FROM yelp_rating_snapshots
       WHERE tenant_id = ?
         AND integration_id = ?
         AND business_id = ?
         AND snapshot_date < ?
       ORDER BY snapshot_date DESC
       LIMIT 1`,
    )
    .bind(input.tenantId, input.integrationId, input.businessId, input.today)
    .first<{
      id: number;
      tenant_id: string;
      integration_id: string;
      business_id: string;
      snapshot_date: string;
      rating: number;
      review_count: number;
      captured_at: string;
    }>();

  if (!row) return null;
  return {
    id: row.id,
    tenantId: row.tenant_id,
    integrationId: row.integration_id,
    businessId: row.business_id,
    snapshotDate: row.snapshot_date,
    rating: row.rating,
    reviewCount: row.review_count,
    capturedAt: row.captured_at,
  };
}
